import type { GetServerSideProps } from 'next';
import { renderToString } from 'react-dom/server';
import { getServerState } from 'react-instantsearch-core';

import { parseUrlFiltersToEnFields } from '@/utils/algolia';
import { getLocaleFromParams } from '@/utils/locale';

import {
  applyProductConfigurationFilters,
  buildUiStateFromURL,
  getIndexName,
  ProductConfigurationFilterType,
} from '@/integrations/algolia/helpers';
import { createSearchClient } from '@/integrations/algolia/searchClientBuilder';
import { PAGE_CONTEXT } from '@/integrations/algolia/types';
import { fetchListingPageData } from '@/integrations/contentful';
import { getUserTokenForAlgoliaSSR } from '@/lib/algoliaUserToken';
import { commonServerSideProps } from '@/lib/commonPageStaticProps';
import { NEXT_PUBLIC_DEFAULT_APP_LOCALE } from '@/lib/const';
import { resolveContentDraftMode } from '@/lib/draftMode';
import type { LocaleMessages } from '@/lib/intl';
import { isSupportedLocale } from '@/lib/locales/locales';
import {
  getContentfulRegionFromLocale,
  getRegionFromLocale,
} from '@/lib/regions';
import { CacheHeaderType, setCacheHeader } from '@/lib/revalidate';
import type { CollectionViewProps } from '@/views/CollectionView';
import { CollectionView } from '@/views/CollectionView';

import { DummyGenericWatchesPage } from '../watches/[...slug]';

export const getServerSideProps: GetServerSideProps<
  CollectionViewProps & {
    localeMessages: LocaleMessages;
  }
> = commonServerSideProps(async (context) => {
  const { params, query, resolvedUrl } = context;
  const draftMode = resolveContentDraftMode(context);

  const routerLocale = getLocaleFromParams(context?.params);

  if (!isSupportedLocale(routerLocale)) {
    return { notFound: true };
  }

  try {
    const slug = params?.slug ?? '';
    const region = getRegionFromLocale(routerLocale);
    const contentfulLocale = region.locale.contentful ?? region.locale.locale;
    const indexName = getIndexName(
      'PRODUCT',
      routerLocale ?? NEXT_PUBLIC_DEFAULT_APP_LOCALE,
    );
    let collection = slug[0]
      .replace('-', ' ')
      .split(' ')
      .map((word) => word.charAt(0).toUpperCase() + word.slice(1))
      .join(' ');
    if (collection.toLowerCase() === 'classic avi') {
      collection = 'Classic AVI';
    }

    const { countryCode } = getContentfulRegionFromLocale(routerLocale);

    const {
      algoliaCategoryId,
      algoliaRuleName,
      cmsData: collectionCmsData,
      seoConfigBuilder,
    } = await fetchListingPageData(
      'watch collection',
      contentfulLocale,
      collection ?? '',
      null,
      null,
      countryCode,
      region.locale.code,
      draftMode,
    );

    let originalFilters = `collection:'${collection}'`;

    const algoliaCategoryIdIsNotNullOrEmpty =
      algoliaCategoryId && algoliaCategoryId !== '';

    if (algoliaCategoryIdIsNotNullOrEmpty) {
      originalFilters = algoliaCategoryId
        ? `category_page_id: '${algoliaCategoryId}'`
        : '';
    }

    const algoliaFilters = applyProductConfigurationFilters(
      ProductConfigurationFilterType.PLP,
      'Watch',
      draftMode,
      originalFilters,
    );

    // Build UI state from URL for InstantSearch (needed for UI state management)
    const initialUiState = buildUiStateFromURL(
      resolvedUrl,
      indexName,
      undefined,
      undefined,
      region.locale,
    );

    // Parse URL query parameters and build filters using the English locale field for this
    // country. The Algolia index for each channel stores English values under a locale-specific
    // attribute (e.g. case.EN_US for us-usd, case.EN_GB for pt-eur). We derive the correct
    // English locale code from the country's supported locales.
    const englishLocale = region.country.locales.find((l) => l.sfCode === 'EN');
    const englishLocaleCode = englishLocale?.code ?? 'EN_US';
    const urlFilters = parseUrlFiltersToEnFields(
      resolvedUrl,
      englishLocaleCode,
    );

    // Combine collection filters with URL filters for SSR query
    const combinedFilters = urlFilters
      ? algoliaFilters
        ? `${algoliaFilters} AND ${urlFilters}`
        : urlFilters
      : algoliaFilters;

    const algoliaSsrSearchClient = createSearchClient({
      isPreview: draftMode,
      isSsr: true,
      mockContext: PAGE_CONTEXT.PLP_WATCHES,
    });

    setCacheHeader(CacheHeaderType.NO_CACHE_TAG, context);

    // Generate Algolia user token server-side for consistent SSR/client rendering
    const algoliaUserToken = getUserTokenForAlgoliaSSR(context.req) ?? null;

    // Analytics tags must match between SSR and client to avoid refetch
    const ssrAnalyticsTags = [
      'platform:web',
      'product:watches',
      'page:listing',
      'context:collection',
      'collection:' + collection,
    ];

    // Get server state with a single Algolia query
    // We extract nbPages from the result to validate pagination
    // Use combinedFilters which includes URL filters as hard filters for SSR
    // (since SSR doesn't have refinement list widgets to process initialUiState)
    const serverState = await getServerState(
      <DummyGenericWatchesPage
        algoliaCategoryId={algoliaCategoryId}
        algoliaRuleName={algoliaRuleName}
        analyticsTags={ssrAnalyticsTags}
        client={algoliaSsrSearchClient}
        filters={combinedFilters}
        indexName={indexName}
        locale={region.locale.code}
        userToken={algoliaUserToken ?? undefined}
        {...(algoliaRuleName ? { query: algoliaRuleName } : {})}
      />,
      {
        renderToString,
      },
    );

    // Extract nbPages from the server state to validate pagination
    // This avoids making a separate Algolia query just for pagination validation
    const ssrResults = serverState?.initialResults?.[indexName];
    const nbPages = ssrResults?.results?.[0]?.nbPages ?? 0;

    const pageIndex = query?.page && parseInt(query.page as string);
    if (
      typeof pageIndex === 'number' &&
      (pageIndex > nbPages || pageIndex < 1)
    ) {
      return {
        notFound: true,
      };
    }

    return {
      props: {
        algoliaFilters: combinedFilters,
        algoliaRuleName,
        algoliaUserToken,
        draftMode: draftMode ?? false,
        pageName: collection ?? '',
        instantSearchProps: {
          initialUiState,
          indexName,
          serverState,
        },
        collectionCmsData,
        englishLocaleCode,
        seoConfig: seoConfigBuilder?.build(),
        serverState,
      },
    };
  } catch (error) {
    console.error('Failed to fetch server-side props', error);
    return { notFound: true };
  }
});

export default CollectionView;
